import { useTranslation } from "react-i18next";
import {
  ContactDetailsSectionBreadcrumbs,
  ContactDetailsSectionBreadcrumbsProps,
} from "../ContactDetailsSectionBreadcrumbs";
import { ContactDetailsSections } from "../../../ContactsDialog";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { useMemo, useState } from "react";
import { BanknoteArrowDown, CreditCard } from "lucide-react";

type PaymentSubsection = "LogPurchase" | "Charge" | null;

export interface PaymentSectionProps {
  enduser: TellescopeModelFromName<"endusers">;
}

export const PaymentSection = ({ enduser }: PaymentSectionProps) => {
  const [activeSubsection, setActiveSubsection] = useState<PaymentSubsection>(null);
  const { t } = useTranslation("contacts");

  const { breadcrumbs, actionButtons } = useMemo<
    Pick<ContactDetailsSectionBreadcrumbsProps, "breadcrumbs" | "actionButtons">
  >(() => {
    const breadcrumbs: ContactDetailsSectionBreadcrumbsProps["breadcrumbs"] = [
      {
        title: t("Payment"),
        onClick: activeSubsection ? () => setActiveSubsection(null) : undefined,
      },
    ];

    if (activeSubsection === "LogPurchase") {
      breadcrumbs.push({ title: t("Log Purchase") });
    } else if (activeSubsection === "Charge") {
      breadcrumbs.push({ title: t("Charge Payment") });
    }

    const actionButtons: ContactDetailsSectionBreadcrumbsProps["actionButtons"] = activeSubsection
      ? undefined
      : [
          {
            hoverLabel: t("Log Purchase"),
            onClick: () => setActiveSubsection("LogPurchase"),
            icon: BanknoteArrowDown,
          },
          ...(enduser.stripeCustomerId
            ? [
                {
                  hoverLabel: t("Charge Payment"),
                  onClick: () => setActiveSubsection("Charge"),
                  icon: CreditCard,
                },
              ]
            : []),
        ];

    return { breadcrumbs, actionButtons };
  }, [activeSubsection, enduser.stripeCustomerId, t]);

  return (
    <div className="flex flex-col gap-4 overflow-y-hidden">
      <ContactDetailsSectionBreadcrumbs
        enduserId={enduser.id}
        breadcrumbs={breadcrumbs}
        actionButtons={actionButtons}
      />
      {activeSubsection === "LogPurchase" ? (
        <ContactDetailsSections.LogPurchaseForm
          contactId={enduser.id}
          onSubmit={() => setActiveSubsection(null)}
          onCancel={() => setActiveSubsection(null)}
        />
      ) : activeSubsection === "Charge" ? (
        <ContactDetailsSections.ChargePaymentForm
          contactId={enduser.id}
          onSubmit={() => setActiveSubsection(null)}
          onCancel={() => setActiveSubsection(null)}
        />
      ) : (
        <ContactDetailsSections.PaymentSection contact={enduser} />
      )}
    </div>
  );
};
